/**
 * ResetPasswordPage - set a new password using a reset token
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setDone(true);
        setTimeout(() => navigate('/auth'), 2500);
      } else {
        setError(data.detail || 'Could not reset password. Please request a new link.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <img src="/nlt-radio-logo.png" alt="NLT Radio" className="w-20 h-20 mx-auto mb-6 object-contain" />

        {!token ? (
          <div className="text-center" data-testid="reset-invalid">
            <AlertTriangle className="w-14 h-14 text-[#FF007F] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded' }}>Invalid link</h1>
            <p className="text-[#A1A1AA] text-sm">This reset link is missing or malformed. Please request a new one.</p>
            <button onClick={() => navigate('/forgot-password')} className="mt-8 w-full py-3 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-white font-semibold">
              Request new link
            </button>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-14 h-14 text-[#00F0FF] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded' }}>Password reset!</h1>
            <p className="text-[#A1A1AA] text-sm">Redirecting you to login…</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Unbounded' }}>Set a new password</h1>
            <p className="text-[#A1A1AA] text-sm text-center mb-8">Choose a strong password for your account.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Lock className="w-5 h-5 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password"
                  required
                  minLength={6}
                  className="w-full pl-11 pr-11 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                  data-testid="reset-password-input"
                />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 text-[#52525B] hover:text-white transition-colors">
                  {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="relative">
                <Lock className="w-5 h-5 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                  data-testid="reset-confirm-input"
                />
              </div>
              {error && <p className="text-[#EF4444] text-sm" data-testid="reset-error">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-white font-semibold disabled:opacity-50 flex items-center justify-center"
                data-testid="reset-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
